import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { issueService } from '../services/issue.service';
import {
  IssueStatus,
  IssueType,
  type GetCompanyHierarchyApiRequest,
  type GetCompanyHierarchyApiResponse,
  type IssueWithChildren,
  type UpdateTaskApiResponse,
  type DeleteIssueApiResponse,
  type ImportMsProjectApiResponse,
  type ExportGoalToExcelApiRequest,
} from '@momentum/types';
import { isRestrictedIssue } from '@/shared/utils/issue-guards';

export const useCompanyHierarchy = (
  params: GetCompanyHierarchyApiRequest,
  enabled = true
) => {
  return useQuery({
    queryKey: ['company-hierarchy', params],
    queryFn: () => issueService.getCompanyHierarchy(params),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useTasks = (isInteraTasks = false, enabled = true) => {
  return useQuery({
    queryKey: ['tasks', isInteraTasks],
    queryFn: () => issueService.getUserTasks(isInteraTasks),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Helper function to update a task in the nested hierarchy structure
 */
function updateTaskInHierarchy(
  hierarchy: IssueWithChildren[],
  taskId: string,
  updates: { status: IssueStatus }
): IssueWithChildren[] {
  return hierarchy.map((issue) => {
    // Skip restricted issues
    if (isRestrictedIssue(issue)) {
      // Still check children if they exist
      if (issue.children && issue.children.length > 0) {
        return {
          ...issue,
          children: updateTaskInHierarchy(issue.children, taskId, updates),
        };
      }
      return issue;
    }

    // If this is the task we're updating
    if (issue._id === taskId && issue.type === IssueType.TASK) {
      return {
        ...issue,
        status: updates.status,
        metadata: {
          ...issue.metadata,
          closedAt:
            updates.status === IssueStatus.COMPLETED ? new Date() : undefined,
        },
      } as IssueWithChildren;
    }
    // Recursively update children if they exist
    if (issue.children && issue.children.length > 0) {
      return {
        ...issue,
        children: updateTaskInHierarchy(issue.children, taskId, updates),
      };
    }
    return issue;
  });
}

export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: IssueStatus }) =>
      issueService.updateTaskStatus(id, {
        status,
      }),
    // Optimistic update - update cache immediately before API call completes
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['company-hierarchy'] });

      // Snapshot the previous value for rollback
      const previousHierarchyQueries =
        queryClient.getQueriesData<GetCompanyHierarchyApiResponse>({
          queryKey: ['company-hierarchy'],
        });

      // Optimistically update company-hierarchy cache
      queryClient.setQueriesData<GetCompanyHierarchyApiResponse>(
        { queryKey: ['company-hierarchy'] },
        (old) => {
          if (!old) return old;
          return updateTaskInHierarchy(old, id, { status });
        }
      );

      // Return context with previous values for rollback
      return { previousHierarchyQueries };
    },
    // On error, rollback to previous state
    onError: (error, _variables, context) => {
      console.error('Failed to update task status:', error);

      // Rollback to previous hierarchy state
      if (context?.previousHierarchyQueries) {
        context.previousHierarchyQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // On success, invalidate in background (non-blocking) to ensure consistency
    onSuccess: (updatedTask: UpdateTaskApiResponse) => {
      // Invalidate specific task query
      queryClient.invalidateQueries({
        queryKey: [updatedTask._id],
      });
      // Invalidate user-assigned-goals for one-on-one review
      queryClient.invalidateQueries({ queryKey: ['user-assigned-goals'] });
      // Mark company hierarchy as stale but don't block (non-blocking)
      queryClient.invalidateQueries({
        queryKey: ['company-hierarchy'],
        refetchType: 'none', // Don't refetch immediately - just mark as stale
      });
    },
    // Always refetch in background after mutation settles to ensure consistency
    onSettled: () => {
      // Refetch active queries in background (non-blocking)
      queryClient.refetchQueries({
        queryKey: ['company-hierarchy'],
        type: 'active', // Only refetch active queries
      });
    },
  });
};

export const useDeleteIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string): Promise<DeleteIssueApiResponse> =>
      issueService.deleteIssue(id),
    onSuccess: () => {
      // Invalidate company hierarchy queries to refresh goals tree
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      // Also invalidate goal lists for other views
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      console.error('Failed to delete issue:', error);
    },
  });
};

export const useImportMsProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File): Promise<ImportMsProjectApiResponse> =>
      issueService.importMsProject(file),
    onSuccess: () => {
      // Invalidate company hierarchy queries to refresh goals tree
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      // Also invalidate goal and task lists
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      // Invalidate user-assigned-goals for one-on-one review
      queryClient.invalidateQueries({ queryKey: ['user-assigned-goals'] });
    },
    onError: (error) => {
      console.error('Failed to import MS Project file:', error);
    },
  });
};

export const useExportGoalToExcel = () => {
  return useMutation({
    mutationFn: ({
      goalId,
      data,
    }: {
      goalId: string;
      data: ExportGoalToExcelApiRequest;
    }): Promise<Blob> => issueService.exportGoalToExcel(goalId, data),
    onError: (error) => {
      console.error('Failed to export goal to Excel:', error);
    },
  });
};
